import { useAuth } from "@clerk/react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import type { ReactElement, ReactNode } from "react";
import { authenticatedApi } from "@/lib/api";
import { BreadcrumbSetter } from "@/components/v2/breadcrumb";
import { QueryErrorState } from "@/components/v2/query-error-state";
import { ScorecardStandalone } from "@/components/v2/scorecard-standalone";
import { useShellGeometry } from "@/components/v2/shell-layout";
import type { SessionLinkState } from "@/components/v2/patient-link-chip";
import type { AnalysisStatus } from "../../../shared/analysis-types";

interface SessionDetailData {
  id: string;
  shortId: string;
  rdId: string;
  clientName: string | null;
  sessionDate: string | null;
  sessionType: "initial" | "follow_up" | null;
  promptVersion: string;
  status: AnalysisStatus;
  errorMessage: string | null;
  addedByUserName: string;
  rd: { id: string; slug: string; name: string };
  canRerun: boolean;
  patientLink: SessionLinkState;
}

function CenteredMessage({ message }: { message: string }): ReactElement {
  return (
    <div className="flex items-center justify-center h-full">
      <p className="text-muted-foreground">{message}</p>
    </div>
  );
}

/**
 * Thin wrapper mirroring the legacy standalone session page: fetch the
 * session (with its joined RD), guard the URL's RD against the session's
 * actual RD, then hand everything to ScorecardStandalone.
 */
export function SessionDetail(): ReactElement | null {
  const { rdId, sessionId } = useParams({ strict: false }) as {
    rdId: string;
    sessionId: string;
  };
  const { getToken } = useAuth();

  const session = useQuery({
    queryKey: ["session", sessionId],
    queryFn: () =>
      authenticatedApi<{ data: SessionDetailData }>(
        getToken,
        `/api/sessions/${sessionId}`
      ),
    retry: false,
  });

  // Legacy resolved the URL's RD segment before checking session ownership, so
  // a nonexistent RD segment reads "RD not found" rather than the mismatch
  // copy. Only look the segment up when the session's RD does not match.
  const sessionRd = session.data?.data.rd;
  const rdMismatch =
    sessionRd !== undefined && sessionRd.id !== rdId && sessionRd.slug !== rdId;
  const urlRd = useQuery({
    queryKey: ["rd", rdId],
    queryFn: () => authenticatedApi<{ data: unknown }>(getToken, `/api/rds/${rdId}`),
    enabled: rdMismatch,
    retry: false,
  });

  // The scorecard's resizable split needs a definite height for its own panels
  // to fill and scroll independently: the viewport under the shell's top bar.
  const frame = useShellGeometry().pageFrame;

  return <div className={frame}>{sessionFrameContent(session, rdMismatch, urlRd)}</div>;
}

// Every state renders inside the same frame element, so these helpers return
// only what goes in it: nothing while a read is pending, the error state, the
// mismatch copy, or the scorecard.
function sessionFrameContent(
  session: UseQueryResult<{ data: SessionDetailData }>,
  rdMismatch: boolean,
  urlRd: UseQueryResult<{ data: unknown }>
): ReactNode {
  if (session.isPending) return null;

  if (session.isError) {
    return (
      <QueryErrorState
        error={session.error}
        notFoundText="Session not found"
        onRetry={() => void session.refetch()}
        className="h-full"
      />
    );
  }

  // Legacy resolved the URL's RD segment (slug or id) and 404ed when the
  // session belonged to a different RD. Compare via the joined rd object,
  // distinguishing a nonexistent RD segment ("RD not found") from a real
  // cross-RD mismatch ("Session does not belong to this RD") like legacy.
  if (rdMismatch) return rdMismatchContent(urlRd);

  return readySessionContent(session.data.data);
}

function rdMismatchContent(urlRd: UseQueryResult<{ data: unknown }>): ReactNode {
  if (urlRd.isPending) return null;

  if (urlRd.isError) {
    return (
      <QueryErrorState
        error={urlRd.error}
        notFoundText="RD not found"
        onRetry={() => void urlRd.refetch()}
        className="h-full"
      />
    );
  }

  return <CenteredMessage message="Session does not belong to this RD" />;
}

function sessionCrumbLabel(data: SessionDetailData): string {
  if (!data.clientName) return data.sessionDate ?? "Session";

  return data.sessionDate
    ? `${data.clientName} · ${data.sessionDate}`
    : data.clientName;
}

function readySessionContent(data: SessionDetailData): ReactNode {
  return (
    <>
      <BreadcrumbSetter
        segments={[
          { label: "RDs", href: "/rds" },
          { label: data.rd.name, href: `/rds/${data.rd.slug}` },
          { label: sessionCrumbLabel(data) },
        ]}
      />
      <ScorecardStandalone
        sessionId={data.id}
        clientName={data.clientName}
        sessionDate={data.sessionDate}
        sessionType={data.sessionType}
        promptVersion={data.promptVersion}
        initialStatus={data.status}
        initialErrorMessage={data.errorMessage}
        rdId={data.rd.id}
        rdSlug={data.rd.slug}
        rdName={data.rd.name}
        addedByUserName={data.addedByUserName}
        canRerun={data.canRerun}
        patientLink={data.patientLink}
      />
    </>
  );
}
